import { BreakpointObserver } from '@angular/cdk/layout';
import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const FALLBACK_BREAKPOINT_PX = {
  md: 600,
  mdLg: 821,
  lg: 960,
  lgXl: 1200,
  xl: 1800,
} as const;

const BREAKPOINT_VAR = {
  md: '--bp-md',
  mdLg: '--bp-md-lg',
  lg: '--bp-lg',
  lgXl: '--bp-lg-xl',
  xl: '--bp-xl',
} as const;

const maxWidth = (value: number) => `(max-width: ${value - 0.02}px)`;
const minWidth = (value: number) => `(min-width: ${value}px)`;

const getCssBreakpointPx = (variableName: string, fallback: number): number => {
  if (typeof document === 'undefined') {
    return fallback;
  }

  const rawValue = getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
  const parsedValue = Number.parseFloat(rawValue);

  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const getMobileBp = (): string =>
  maxWidth(getCssBreakpointPx(BREAKPOINT_VAR.md, FALLBACK_BREAKPOINT_PX.md));
const getMdUpBp = (): string =>
  minWidth(getCssBreakpointPx(BREAKPOINT_VAR.md, FALLBACK_BREAKPOINT_PX.md));
const getMdLgUpBp = (): string =>
  minWidth(
    getCssBreakpointPx(BREAKPOINT_VAR.mdLg, FALLBACK_BREAKPOINT_PX.mdLg)
  );
const getLgUpBp = (): string =>
  minWidth(getCssBreakpointPx(BREAKPOINT_VAR.lg, FALLBACK_BREAKPOINT_PX.lg));
const getLgXlUpBp = (): string =>
  minWidth(
    getCssBreakpointPx(BREAKPOINT_VAR.lgXl, FALLBACK_BREAKPOINT_PX.lgXl)
  );
const getXlUpBp = (): string =>
  minWidth(getCssBreakpointPx(BREAKPOINT_VAR.xl, FALLBACK_BREAKPOINT_PX.xl));

/**
 * Centralized responsive state management service.
 * Provides reactive screen size signals throughout the application
 * to eliminate duplication and maintain a single source of truth.
 *
 * Runtime breakpoint sources:
 * - `isXSmall`: CSS var `--bp-md` as `max-width: md - 0.02px`
 * - `isSmall`: CSS var `--bp-md` (fallback 600px)   * - `isMdLarge`: CSS var `--bp-md-lg` (fallback 820px) * - `isLarge`: CSS var `--bp-lg` (fallback 960px)
 * - `isLargeDesktop`: CSS var `--bp-lg-xl` (fallback 1200px)
 * - `isXLDesktop`: CSS var `--bp-xl` (fallback 1800px)
 */
@Injectable({
  providedIn: 'root',
})
export class ResponsiveService {
  private breakpointObserver = inject(BreakpointObserver);
  private destroyRef = inject(DestroyRef);
  private xSmallBreakpoint = getMobileBp();
  private mdUpBreakpoint = getMdUpBp();
  private mdLgUpBreakpoint = getMdLgUpBp();
  private lgUpBreakpoint = getLgUpBp();
  private lgXlUpBreakpoint = getLgXlUpBp();
  private xlUpBreakpoint = getXlUpBp();

  isXSmall = signal(false);
  isSmall = signal(false);
  isMdLarge = signal(false);
  isLarge = signal(false);
  isLargeDesktop = signal(false);
  isXLDesktop = signal(false);

  constructor() {
    this.initializeBreakpointObserver();
  }

  private initializeBreakpointObserver(): void {
    this.breakpointObserver
      .observe([
        this.xSmallBreakpoint,
        this.mdUpBreakpoint,
        this.mdLgUpBreakpoint,
        this.lgUpBreakpoint,
        this.lgXlUpBreakpoint,
        this.xlUpBreakpoint,
      ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.isXSmall.set(result.breakpoints[this.xSmallBreakpoint]);
        this.isSmall.set(result.breakpoints[this.mdUpBreakpoint]);
        this.isMdLarge.set(result.breakpoints[this.mdLgUpBreakpoint]);
        this.isLarge.set(result.breakpoints[this.lgUpBreakpoint]);
        this.isLargeDesktop.set(result.breakpoints[this.lgXlUpBreakpoint]);
        this.isXLDesktop.set(result.breakpoints[this.xlUpBreakpoint]);
      });
  }
}
